/**
 * Bulk Import API
 * Handles CSV/Excel file uploads and bulk data import operations
 */

// Import API configuration directly to avoid circular dependency
const API_CONFIG = {
  baseURL: `${(import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/$/, '')}/api/v1`,
  timeout: 30000,
};

// Storage keys for authentication
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'soma_access_token',
  REFRESH_TOKEN: 'soma_refresh_token',
};

/**
 * Simple API client for bulk import operations
 */
class BulkImportAPIClient {
  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.timeout = API_CONFIG.timeout;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    
    const config = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      ...options,
    };

    // Don't set Content-Type for FormData
    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle blob responses
      if (options.responseType === 'blob') {
        return await response.blob();
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async get(endpoint, options = {}) {
    const { params, ...restOptions } = options;
    let url = endpoint;
    
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value);
        }
      });
      url += `?${searchParams.toString()}`;
    }

    return this.request(url, { method: 'GET', ...restOptions });
  }

  async post(endpoint, data, options = {}) {
    const config = {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
      ...options,
    };

    return this.request(endpoint, config);
  }
}

const apiClient = new BulkImportAPIClient();

export const bulkImportAPI = {
  /**
   * Upload and process CSV/Excel file for bulk import
   */
  async uploadFile(file, entityType, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityType', entityType);
    
    Object.keys(options).forEach(key => {
      formData.append(key, options[key]);
    });

    return apiClient.post('/bulk-import/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  /**
   * Get CSV template for entity type
   */
  async getTemplate(entityType) {
    return apiClient.get(`/bulk-import/template/${entityType}`, {
      responseType: 'blob'
    });
  },

  /**
   * Get supported file formats
   */
  async getSupportedFormats() {
    return apiClient.get('/bulk-import/supported-formats');
  },

  /**
   * Get import statistics
   */
  async getStatistics(params = {}) {
    return apiClient.get('/bulk-import/statistics', { params });
  },

  /**
   * Validate file before import
   */
  async validateFile(file, entityType, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityType', entityType);
    
    Object.keys(options).forEach(key => {
      formData.append(key, options[key]);
    });

    return apiClient.post('/bulk-import/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  /**
   * Get help documentation
   */
  async getHelp() {
    return apiClient.get('/bulk-import/help');
  }
};

export default bulkImportAPI;